import { useEffect, useId, useState } from 'react';
import {
  checklistStorageKey,
  promptKey,
  readChecklistState,
  writeChecklistState,
  type ChecklistDeviceState,
} from '../lib/reviewChecklistState';

interface Props {
  /** Resolved prompts from the server (1 to 4). */
  prompts: string[];
  slug: string;
  /** The round; a new round is a new storage key, so it starts unticked. */
  status: string;
  /** Henry's admin preview: unticked, non-interactive, nothing stored. */
  preview?: boolean;
}

/**
 * C8 — "What we need from you". A short brief under the "Your review" card so
 * reviewers know what this round is for. Ticks and the collapsed state stay on
 * this device (P-f); nothing here blocks Approve or Send for review.
 */
export default function ReviewChecklistCard({ prompts, slug, status, preview = false }: Props) {
  const storageKey = checklistStorageKey(slug, status);
  const [state, setState] = useState<ChecklistDeviceState>(() =>
    preview ? { collapsed: false, ticked: [] } : readChecklistState(storageKey),
  );
  const bodyId = useId();

  // A round change while the page is open (approve, refresh) reads that
  // round's own state.
  useEffect(() => {
    if (!preview) setState(readChecklistState(storageKey));
  }, [storageKey, preview]);

  function update(next: ChecklistDeviceState) {
    setState(next);
    if (!preview) writeChecklistState(storageKey, next);
  }

  const keys = prompts.map(promptKey);
  const tickedCount = keys.filter((k) => state.ticked.includes(k)).length;
  const allDone = tickedCount === prompts.length;

  function toggle(key: string) {
    if (preview) return;
    // Drop ticks for prompts no longer shown, so an edited prompt can't
    // leave a stale tick behind.
    const current = state.ticked.filter((k) => keys.includes(k));
    update({
      ...state,
      ticked: current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    });
  }

  const grid =
    prompts.length >= 4
      ? 'sm:grid-cols-2 min-[64.0625rem]:grid-cols-4'
      : prompts.length === 3
        ? 'sm:grid-cols-2 min-[64.0625rem]:grid-cols-3'
        : prompts.length === 2
          ? 'sm:grid-cols-2'
          : '';

  return (
    <div
      data-print-hide
      className="bg-card border border-line rounded-md overflow-hidden"
    >
      <div
        className={`flex items-center gap-3.5 pl-[22px] pr-5 py-3.5 ${
          allDone ? 'bg-accent/[0.04]' : ''
        }`}
      >
        <div className="flex items-baseline gap-x-3 gap-y-1 flex-wrap min-w-0 flex-1">
          <h3 className="font-display font-semibold text-[17px] leading-[1.2] tracking-[-0.01em] text-fg">
            What we need from you
          </h3>
          {allDone ? (
            <span className="text-[13px] text-muted">
              <b className="text-accent font-semibold">All {prompts.length} checked.</b>{' '}
              When you're happy, approve or send your feedback to Henry.
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-faint whitespace-nowrap">
              <b className="text-accent font-semibold">{tickedCount}</b> of {prompts.length}
              <span className="max-sm:hidden"> checked</span>
            </span>
          )}
        </div>
        <button
          type="button"
          aria-expanded={!state.collapsed}
          aria-controls={bodyId}
          onClick={() => update({ ...state, collapsed: !state.collapsed })}
          className="
            shrink-0 inline-flex items-center gap-1.5 px-2 py-1.5 rounded-[6px]
            font-mono text-[10px] uppercase tracking-[0.12em] text-muted
            hover:text-fg hover:bg-elevated transition-colors
            focus:outline-none focus-visible:shadow-focus
          "
        >
          {state.collapsed ? 'Show' : 'Hide'}
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className={`motion-safe:transition-transform motion-safe:duration-300 ${
              state.collapsed ? '-rotate-90' : ''
            }`}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>

      <div id={bodyId} hidden={state.collapsed} className="border-t border-line px-2.5 pt-1.5 pb-2.5">
        <ul className={`grid grid-cols-1 gap-1 ${grid}`}>
          {prompts.map((prompt, i) => {
            const on = state.ticked.includes(keys[i]);
            return (
              <li
                key={`${keys[i]}-${i}`}
                role="checkbox"
                aria-checked={on}
                aria-disabled={preview || undefined}
                tabIndex={preview ? -1 : 0}
                onClick={() => toggle(keys[i])}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    toggle(keys[i]);
                  }
                }}
                className={`
                  flex items-start gap-[11px] min-h-11 px-3 py-[11px] rounded-sm
                  motion-safe:transition-colors motion-safe:duration-200
                  focus:outline-none focus-visible:shadow-focus
                  ${preview ? '' : 'cursor-pointer hover:bg-elevated'}
                `}
              >
                <span
                  aria-hidden
                  className={`
                    shrink-0 mt-px w-5 h-5 rounded-[6px] border-[1.5px] inline-flex items-center justify-center
                    motion-safe:transition-colors motion-safe:duration-200
                    ${on ? 'bg-accent border-accent text-accent-fg' : 'bg-card border-faint/60'}
                  `}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`motion-safe:transition-opacity motion-safe:duration-200 ${on ? 'opacity-100' : 'opacity-0'}`}
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <span className={`text-[14px] leading-[1.45] font-medium ${on ? 'text-muted' : 'text-fg'}`}>
                  {prompt}
                </span>
              </li>
            );
          })}
        </ul>
        {!allDone && (
          <p className="flex items-center gap-2 px-3 pt-2 pb-0.5 text-[12px] text-faint">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden
              className="shrink-0"
            >
              <rect x="5" y="2" width="14" height="20" rx="2.5" />
              <path d="M11 18h2" strokeLinecap="round" />
            </svg>
            <span>
              Your ticks stay on this device<span className="max-sm:hidden"> to help you keep track</span>.
              {' '}Henry doesn't see them.
            </span>
          </p>
        )}
      </div>
    </div>
  );
}
